import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import { ChartFrame } from '../charts/ChartFrame';
import { defaultTemplate } from '../charts/defaults';
import { TEMPLATES, getTemplate, suggestTemplate } from '../charts/registry';
import { ChartError, ChartLoading } from '../charts/states';
import { SignedOut } from '../components/SignedOut';
import { useChartData } from '../components/ChartRenderer';
import { embedParams } from '../embedParams';
import type { CatalogChart } from '../types';

/**
 * One catalogue query, drawn on demand: `/c/<chartId>?app_id=…&form=trend`.
 *
 * Every other route needs something saved first — `/d/<slug>` a dashboard,
 * `/w/<id>` a widget. This one needs nothing. That is what lets another surface
 * (the AI CoFounder, most immediately) ask for a chart it has just decided to
 * show, without a round trip through an author saving it.
 *
 * It is deliberately not an authoring route. Nothing here is persisted; the URL
 * is the whole definition, which means a chart shown this way can be linked,
 * bookmarked and reproduced exactly.
 */

interface Props {
  chartId: string;
}

/**
 * Params this route consumes itself. Everything else on the URL is a query
 * parameter and travels upstream — the same denylist rule the embed uses, for
 * the same reason: the platform is free to add parameters without this app
 * shipping a change.
 */
const OWN_PARAMS = new Set(['form', 'source', 'title']);

export const ChartView = ({ chartId }: Props) => {
  const [catalog, setCatalog] = useState<CatalogChart[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const url = embedParams();
  const source = url.source || 'appstore';
  const explicitForm = url.form;
  const explicitTitle = url.title;

  const params = useMemo(() => {
    const forwarded: Record<string, string> = {};
    for (const [key, value] of Object.entries(url)) {
      if (!OWN_PARAMS.has(key)) forwarded[key] = value;
    }
    return forwarded;
    // The URL does not change without a reload on this route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api
      .catalog(source)
      .then(setCatalog)
      .catch((err: Error) => setCatalogError(err.message));
  }, [source]);

  const { data, error, status, stale, retry } = useChartData(source, chartId, params);

  /**
   * Three sources, most specific first: an explicit `form` on the URL, then the
   * catalogue's documented default for this chart, then the shape heuristic.
   *
   * The middle step is what makes a bare `/c/9` right rather than merely
   * plausible — the data team's "Default viz" column says chart 9 is an area
   * chart, and no amount of looking at columns and rows would reliably infer
   * that over a plain line.
   */
  // An unknown `form` is ignored rather than honoured. getTemplate falls back to
  // the first template, so passing one the registry does not have — `area`, say,
  // which is the data team's word for chart 9 but not an id here — would quietly
  // draw a bar chart and skip the documented default. Falling through is the
  // better failure: the caller still gets the right shape.
  const requested = explicitForm && TEMPLATES.some((t) => t.id === explicitForm) ? explicitForm : '';
  const form = requested || defaultTemplate(chartId) || (data ? suggestTemplate(data) : 'table');

  // A 401 is a state, not a fault: explain it and say how to get a session.
  if (status === 401) return <SignedOut context="embed" />;
  if (catalogError) return <ChartError title="Chart unavailable" message={catalogError} />;
  if (error) return <ChartError message={error} onRetry={retry} />;
  if (!data || !catalog) return <ChartLoading />;

  const entry = catalog.find((c) => String(c.chartId) === String(chartId));
  if (!entry) {
    return (
      <ChartError
        title="No such chart"
        message={`'${chartId}' is not in the ${source} catalogue. It may have been renumbered.`}
      />
    );
  }

  const Template = getTemplate(form).Component;

  return (
    <div className="chart-view">
      <ChartFrame
        title={explicitTitle || entry.title}
        description={entry.description}
        data={data}
      >
        <div className="chart-card__plot" data-stale={stale || undefined}>
          <Template data={data} />
        </div>
      </ChartFrame>
    </div>
  );
};
